const dsteem = require('dsteem');
const steem = require('steem');

steem.api.setOptions({ url: 'https://api.steemit.com' });
if (steem.config) {
  steem.config.set('websocket', 'https://api.steemit.com');
  steem.config.set('address_prefix', 'STM');
  steem.config.set('chain_id', '0000000000000000000000000000000000000000000000000000000000000000');
}
const client = new dsteem.Client('https://api.steemit.com');

/**
 * Validates a user's login with Private Posting Key (or Master Password)
 * @param {string} user Steem username
 * @param {string} password WIF private key or master password
 * @returns {Promise<{success: boolean, message: string, username?: string, wif?: string, account?: object}>}
 */
export async function loginWithWif(user, password) {
  return new Promise(async (resolve) => {
    if (!user || !password) {
      return resolve({ success: false, message: "Please provide both username and private key." });
    }

    const username = user.trim().toLowerCase().replace('@', '');
    const keyInput = password.trim();

    let account;
    try {
      const accounts = await client.database.getAccounts([username]);
      if (accounts && accounts.length > 0) {
        account = accounts[0];
      } else {
        return resolve({ success: false, message: `Account @${username} does not exist.` });
      }
    } catch (err) {
      try {
        const accounts = await new Promise((res, rej) => {
          steem.api.getAccounts([username], (e, r) => e ? rej(e) : res(r));
        });
        if (accounts && accounts.length > 0) {
          account = accounts[0];
        } else {
          return resolve({ success: false, message: `Account @${username} does not exist.` });
        }
      } catch (steemErr) {
        console.error("Error fetching account:", steemErr);
        return resolve({ success: false, message: "Network error connecting to Steem blockchain." });
      }
    }

    const postingpub = account.posting.key_auths[0][0];
    const activepub = account.active.key_auths[0][0];
    const ownerpub = account.owner.key_auths[0][0];
    const memopub = account.memo_key;

    // Check if input is a direct WIF key
    let isPosting = false;
    let isActive = false;
    let isOwner = false;
    let isMemo = false;

    // Test with dsteem PrivateKey
    try {
      const priv = dsteem.PrivateKey.fromString(keyInput);
      const pub = priv.createPublic('STM').toString();
      if (pub === postingpub) isPosting = true;
      if (pub === activepub) isActive = true;
      if (pub === ownerpub) isOwner = true;
      if (pub === memopub) isMemo = true;
    } catch (e) {
      // Not a standard single WIF or invalid format
    }

    // Test with steem.auth.wifIsValid safely
    if (!isPosting && !isActive && !isOwner && !isMemo) {
      try { if (steem.auth.wifIsValid(keyInput, postingpub)) isPosting = true; } catch (e) {}
      try { if (steem.auth.wifIsValid(keyInput, activepub)) isActive = true; } catch (e) {}
      try { if (steem.auth.wifIsValid(keyInput, ownerpub)) isOwner = true; } catch (e) {}
      try { if (steem.auth.wifIsValid(keyInput, memopub)) isMemo = true; } catch (e) {}
    }

    // Check if input might be a master password
    let derivedPostingWif = keyInput;
    if (!isPosting && !isActive && !isOwner && !isMemo) {
      try {
        const keys = steem.auth.getPrivateKeys(username, keyInput, ['posting', 'active', 'owner', 'memo']);
        if (keys) {
          if (keys.postingPubkey === postingpub) {
            isPosting = true;
            derivedPostingWif = keys.posting;
          }
          if (keys.activePubkey === activepub) isActive = true;
          if (keys.ownerPubkey === ownerpub) isOwner = true;
        }
      } catch (e) {
        // Password derivation failed
      }
    }

    if (isOwner) {
      return resolve({
        success: false,
        message: "You are using your Owner key, which is not permitted. Please use your Private Posting Key."
      });
    }

    if (isMemo) {
      return resolve({
        success: false,
        message: "You are using your Memo key. Please use your Private Posting Key."
      });
    }

    if (isPosting || isActive) {
      return resolve({
        success: true,
        message: "Logged in successfully.",
        username: username,
        wif: derivedPostingWif,
        account: account
      });
    }

    return resolve({
      success: false,
      message: "Wrong Private Key or password for @" + username
    });
  });
}
